import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Wallet, ChevronDown, ChevronUp, Users, History } from 'lucide-react';
import {
  useOperatorWallet,
  useAllocateLicenses,
  useLicensePacks,
  useCreateLicensePack,
  useUpdateLicensePack,
  useDeactivateLicensePack,
  useAssignLicensePack,
  useAdminUsers,
  useLicenseLedger,
} from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { SectionHeader, Spinner } from '../../components/ui';
import { toast } from '../../store';

// ─── Pack form ──────────────────────────────────────────────────────────────────

const EMPTY_PACK = { name: '', description: '', license_qty: 10, price: 0 };

function PackForm({ initial = EMPTY_PACK, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, license_qty: Number(form.license_qty), price: Number(form.price) });
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 text-xs">
      <div className="col-span-2">
        <label className="block text-txt-muted mb-1">Pack Name</label>
        <input className="input-field w-full" value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="e.g. Starter Pack" />
      </div>
      <div className="col-span-2">
        <label className="block text-txt-muted mb-1">Description (optional)</label>
        <textarea className="input-field w-full resize-none" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div>
        <label className="block text-txt-muted mb-1">Number of Licenses</label>
        <input type="number" min={1} className="input-field w-full" value={form.license_qty} onChange={(e) => set('license_qty', e.target.value)} required />
      </div>
      <div>
        <label className="block text-txt-muted mb-1">Price (₹)</label>
        <input type="number" min={0} className="input-field w-full" value={form.price} onChange={(e) => set('price', e.target.value)} />
      </div>
      <div className="col-span-2 flex gap-2 pt-1">
        <button type="submit" className="btn-primary text-xs" disabled={saving}>
          {saving ? 'Saving…' : 'Save Pack'}
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export default function AdminLicensePacksPage() {
  const currentUser = useSessionStore((s) => s.user);

  const { data: allUsers = [] } = useAdminUsers();
  const operators = allUsers.filter((u) => u.role === 'operator' || u.roles?.includes('operator'));
  const [selectedOpId, setSelectedOpId] = useState(null);

  useEffect(() => {
    if (!selectedOpId && operators.length > 0) setSelectedOpId(operators[0].id);
  }, [operators.length]); // eslint-disable-line

  const walletOpId = selectedOpId;
  const { data: wallet, isLoading: walletLoading } = useOperatorWallet(walletOpId);
  const { data: packs = [], isLoading: packsLoading } = useLicensePacks({ include_inactive: true });

  const { data: ledger = [], isLoading: ledgerLoading } = useLicenseLedger(walletOpId);
  const [showLedger, setShowLedger] = useState(false);

  const allocate = useAllocateLicenses();
  const createPack = useCreateLicensePack();
  const updatePack = useUpdateLicensePack();
  const deactivatePack = useDeactivateLicensePack();
  const assignPack = useAssignLicensePack();

  const [showNewPack, setShowNewPack] = useState(false);
  const [editingPackId, setEditingPackId] = useState(null);
  const [assignPackId, setAssignPackId] = useState('');
  const [assignOpId, setAssignOpId] = useState('');
  const [allocateAmt, setAllocateAmt] = useState('');
  const [allocateNote, setAllocateNote] = useState('');
  const [showAllocate, setShowAllocate] = useState(false);

  const handleAllocate = async (e) => {
    e.preventDefault();
    const amt = Number(allocateAmt);
    if (!amt || amt < 1 || !walletOpId) return;
    try {
      await allocate.mutateAsync({ opId: walletOpId, body: { amount: amt, note: allocateNote || undefined } });
      toast.success(`${amt} licenses allocated`);
      setAllocateAmt('');
      setAllocateNote('');
      setShowAllocate(false);
    } catch (err) {
      toast.error(err.message || 'Failed to allocate licenses');
    }
  };

  const handleCreatePack = async (body) => {
    try {
      await createPack.mutateAsync(body);
      toast.success('License pack created');
      setShowNewPack(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create pack');
    }
  };

  const handleUpdatePack = async (id, body) => {
    try {
      await updatePack.mutateAsync({ id, body });
      toast.success('Pack updated');
      setEditingPackId(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update pack');
    }
  };

  const handleDeactivatePack = async (id) => {
    try {
      await deactivatePack.mutateAsync(id);
      toast.success('Pack deactivated');
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate pack');
    }
  };

  const handleAssignPack = async (e) => {
    e.preventDefault();
    if (!assignPackId || !assignOpId) return;
    try {
      await assignPack.mutateAsync({ packId: Number(assignPackId), body: { operator_id: Number(assignOpId) } });
      const pack = packs.find((p) => p.id === Number(assignPackId));
      const op = operators.find((u) => u.id === Number(assignOpId));
      toast.success(`"${pack?.name}" assigned to ${op?.name || op?.email || assignOpId}`);
      setAssignPackId('');
      setAssignOpId('');
    } catch (err) {
      toast.error(err.message || 'Failed to assign pack');
    }
  };

  const available = (wallet?.total_licenses ?? 0) - (wallet?.used_licenses ?? 0);
  const selectedOp = operators.find((u) => u.id === selectedOpId);

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <SectionHeader title="License Packs" />

      {/* ── License Wallet ── */}
      <div className="border border-bg-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
            <Wallet size={16} />
            License Wallet
          </div>
          <button
            className="btn-ghost text-xs flex items-center gap-1"
            onClick={() => setShowAllocate((v) => !v)}
            disabled={!walletOpId}
          >
            {showAllocate ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Add Licenses
          </button>
        </div>

        <div className="mb-3">
          <label className="text-2xs text-txt-muted block mb-1">
            <Users size={11} className="inline mr-1" />
            Operator
          </label>
          {operators.length === 0 ? (
            <p className="text-xs text-txt-muted">No operators found</p>
          ) : (
            <select
              className="input-field text-xs w-full max-w-xs"
              value={selectedOpId ?? ''}
              onChange={(e) => setSelectedOpId(Number(e.target.value))}
            >
              {operators.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} (UID {u.id})
                </option>
              ))}
            </select>
          )}
        </div>

        {walletLoading ? (
          <Spinner />
        ) : !walletOpId ? (
          <p className="text-xs text-txt-muted">Select an operator to view their wallet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { label: 'Total', value: wallet?.total_licenses ?? 0, cls: 'text-txt-primary' },
              { label: 'Used', value: wallet?.used_licenses ?? 0, cls: 'text-warn' },
              { label: 'Available', value: available, cls: available > 0 ? 'text-bull' : 'text-bear' },
            ].map(({ label, value, cls }) => (
              <div key={label} className="bg-bg-surface rounded p-3">
                <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                <div className="text-2xs text-txt-muted mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        )}

        {showAllocate && walletOpId && (
          <form onSubmit={handleAllocate} className="mt-3 pt-3 border-t border-bg-border flex gap-2 items-end">
            <div>
              <label className="text-2xs text-txt-muted block mb-1">Amount</label>
              <input
                type="number" min={1} className="input-field w-24 text-xs"
                value={allocateAmt} onChange={(e) => setAllocateAmt(e.target.value)} required
              />
            </div>
            <div className="flex-1">
              <label className="text-2xs text-txt-muted block mb-1">Note (optional)</label>
              <input className="input-field w-full text-xs" value={allocateNote} onChange={(e) => setAllocateNote(e.target.value)} />
            </div>
            <button type="submit" className="btn-primary text-xs" disabled={allocate.isPending}>
              {allocate.isPending ? 'Adding…' : 'Add'}
            </button>
          </form>
        )}
      </div>

      {/* ── License Packs ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-txt-primary">License Packs</h3>
          <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setShowNewPack((v) => !v)}>
            <Plus size={14} />
            New Pack
          </button>
        </div>

        {showNewPack && (
          <div className="border border-bg-border rounded p-4 mb-4 bg-bg-surface/50">
            <PackForm onSave={handleCreatePack} onCancel={() => setShowNewPack(false)} saving={createPack.isPending} />
          </div>
        )}

        {packsLoading ? (
          <Spinner />
        ) : packs.length === 0 ? (
          <p className="text-xs text-txt-muted">No license packs yet. Create one above.</p>
        ) : (
          <div className="border border-bg-border rounded overflow-hidden mb-4">
            <table className="w-full text-xs">
              <thead className="bg-bg-surface border-b border-bg-border">
                <tr>
                  {['Name', 'Licenses', 'Price', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-txt-muted font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-border">
                {packs.map((pack) => (
                  <>
                    <tr key={pack.id} className="hover:bg-bg-surface/40">
                      <td className="px-3 py-2 text-txt-primary font-medium">
                        {pack.name}
                        {pack.description && (
                          <div className="text-2xs text-txt-muted font-normal mt-0.5 max-w-xs truncate">{pack.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-txt-muted font-medium">{pack.license_qty}</td>
                      <td className="px-3 py-2 text-txt-muted">₹{pack.price}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-2xs border ${pack.is_active ? 'text-bull bg-bull/10 border-bull/30' : 'text-txt-muted bg-bg-surface border-bg-border'}`}>
                          {pack.is_active ? 'active' : 'inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button className="text-txt-muted hover:text-txt-primary" onClick={() => setEditingPackId(editingPackId === pack.id ? null : pack.id)}>
                            <Edit2 size={13} />
                          </button>
                          {pack.is_active && (
                            <button className="text-txt-muted hover:text-bear" onClick={() => handleDeactivatePack(pack.id)}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {editingPackId === pack.id && (
                      <tr key={`edit-pack-${pack.id}`}>
                        <td colSpan={5} className="px-3 py-3 bg-bg-surface/50">
                          <PackForm
                            initial={{ name: pack.name, description: pack.description ?? '', license_qty: pack.license_qty, price: pack.price }}
                            onSave={(body) => handleUpdatePack(pack.id, body)}
                            onCancel={() => setEditingPackId(null)}
                            saving={updatePack.isPending}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Assign pack to operator */}
        <div className="border border-bg-border rounded p-4 bg-bg-surface/30 mb-6">
          <h4 className="text-xs font-medium text-txt-primary mb-3">Assign Pack to Operator</h4>
          <form onSubmit={handleAssignPack} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-2xs text-txt-muted block mb-1">License Pack</label>
              <select className="input-field text-xs" value={assignPackId} onChange={(e) => setAssignPackId(e.target.value)} required>
                <option value="">Select pack…</option>
                {packs.filter((p) => p.is_active).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — {p.license_qty} licenses</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-2xs text-txt-muted block mb-1">Operator</label>
              <select className="input-field text-xs" value={assignOpId} onChange={(e) => setAssignOpId(e.target.value)} required>
                <option value="">Select operator…</option>
                {operators.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email} (UID {u.id})</option>
                ))}
              </select>
            </div>
            {assignPackId && (
              <div className="text-2xs text-txt-muted self-end pb-2">
                Will credit <span className="text-bull font-medium">{packs.find((p) => p.id === Number(assignPackId))?.license_qty ?? '?'} licenses</span> to wallet
              </div>
            )}
            <button type="submit" className="btn-primary text-xs" disabled={assignPack.isPending || !assignPackId || !assignOpId}>
              {assignPack.isPending ? 'Assigning…' : 'Assign'}
            </button>
          </form>
        </div>
      </div>

      {/* ── Audit History (License Ledger) ── */}
      {walletOpId && (
        <div>
          <button
            className="flex items-center gap-2 text-sm font-medium text-txt-primary mb-3 w-full text-left"
            onClick={() => setShowLedger((v) => !v)}
          >
            <History size={15} />
            Audit History
            {showLedger ? <ChevronUp size={13} className="ml-auto" /> : <ChevronDown size={13} className="ml-auto" />}
          </button>

          {showLedger && (
            ledgerLoading ? <div className="text-xs text-txt-muted">Loading…</div> :
            ledger.length === 0 ? (
              <p className="text-xs text-txt-muted">No ledger entries for this operator.</p>
            ) : (
              <div className="border border-bg-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-bg-surface border-b border-bg-border">
                    <tr>
                      {['Date', 'Type', 'Amount', 'Reference', 'Actor'].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-txt-muted font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bg-border">
                    {ledger.map((entry) => (
                      <tr key={entry.id} className="hover:bg-bg-surface/40">
                        <td className="px-3 py-2 font-mono text-txt-muted whitespace-nowrap">
                          {new Date(entry.created_at + 'Z').toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-2xs border font-mono ${
                            entry.type === 'ALLOCATE' ? 'text-bull bg-bull/10 border-bull/30'
                            : entry.type === 'CONSUME' ? 'text-warn bg-warn/10 border-warn/30'
                            : 'text-txt-muted bg-bg-surface border-bg-border'
                          }`}>
                            {entry.type}
                          </span>
                        </td>
                        <td className={`px-3 py-2 font-medium ${entry.amount > 0 ? 'text-bull' : 'text-bear'}`}>
                          {entry.amount > 0 ? '+' : ''}{entry.amount}
                        </td>
                        <td className="px-3 py-2 text-txt-muted">{entry.reference_id ?? '—'}</td>
                        <td className="px-3 py-2 text-txt-muted">
                          {entry.actor_user_id ? `UID ${entry.actor_user_id}` : 'System'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
